/**
 * Handlebars Loader + Helpers Registry
 * ====================================
 * 
 * Unified Handlebars management for frontend only
 * 
 * Features:
 * - Template loading from API (wab-config)
 * - Helper registration (just-handlebars-helpers + custom)
 * - Memory cache for performance
 * - Safe compilation (no new Function() - CSP compliant)
 */

import Handlebars from 'handlebars';
import JustHandlebarsHelpers from 'just-handlebars-helpers';
import debug from '@commons-shared/debug.js';
import { config } from '../config/environment.js';
import { routedFetch } from '../utils/api-router.js';
import { PATH_AXIS } from './wa-item-class.js';

// Cache mémoire
const templateCache = new Map();
let isHelpersInitialized = false;

// Construire la liste des axes possibles à partir de PATH_AXIS
// Les templates recherchent 'childrenPart', 'parentPart', etc.
const POSSIBLE_AXES = Object.values(PATH_AXIS).map(axis => `${axis}Part`);

/**
 * Analyse le source text et retourne la liste des axes nécessaires
 * @param {string} sourceText - Texte source du template
 * @returns {string} - Axes séparés par virgule (ou chaîne vide)
 */
function analyzeNeededAxes(sourceText) {
  // Gérer les cas null/undefined
  if (!sourceText || typeof sourceText !== 'string') {
    return '';
  }
  
  const foundAxes = [];
  
  for (const axis of POSSIBLE_AXES) {
    // Chercher le nom de l'axe dans le texte source
    if (sourceText.includes(axis)) {
      foundAxes.push(axis);
    }
  }
  
  return foundAxes.join(',');
}

/**
 * Initialise les helpers Handlebars (une seule fois)
 * Enregistre les helpers standard et custom
 */
export async function initializeHandlebarsHelpers() {
  if (isHelpersInitialized) {
    return;
  }

  try {
    debug.log(config, '[Handlebars] Initializing helpers...');

    // Enregistrer les helpers standard de just-handlebars-helpers
    JustHandlebarsHelpers.registerHelpers(Handlebars);

    // === Helpers IA Vision ===
    Handlebars.registerHelper('DescribePortrait', function (theURL) {
      if (!theURL) return '';
      return `@!DescribePortrait!${theURL}!@`;
    });

    Handlebars.registerHelper('DescribeImage', function (theURL) {
      if (!theURL) return '';
      return `@!DescribeImage!${theURL}!@`;
    });

    // === Helpers Data Storage ===
    Handlebars.registerHelper('StoreId', function (theContent) {
      if (!theContent) return '';
      return `@!StoreId!${theContent}!@`;
    });

    Handlebars.registerHelper('CanonText', function (theContent) {
      return theContent || '';
    });

    // === Helpers Utilitaires ===
    Handlebars.registerHelper('TruncateWords', function (text, wordCount) {
      if (!text || !wordCount) return text;
      const words = String(text).split(/\s+/);
      if (words.length > wordCount) {
        return words.slice(0, wordCount).join(' ') + '...';
      }
      return text;
    });

    Handlebars.registerHelper('JSONStringify', function (obj) {
      try {
        return JSON.stringify(obj, null, 2);
      } catch (error) {
        return String(obj);
      }
    });

    Handlebars.registerHelper('SafeHTML', function (html) {
      if (!html) return '';
      return new Handlebars.SafeString(html);
    });

    isHelpersInitialized = true;
    debug.log(config, '[Handlebars] Helpers initialized successfully');
  } catch (error) {
    debug.error(config, '[Handlebars] Error initializing helpers:', error);
    throw error;
  }
}

/**
 * Extrait les métadonnées d'un template depuis un objet hbData
 * Le template 'compiled' est déjà construit par api-router.js
 * 
 * @param {Object} hbData - Données du template depuis l'API
 * @returns {Object} Template structuré avec métadonnées
 */
function extractTemplateMetadata(hbData) {
  return {
    compiled: hbData.compiled || null,
    key: hbData.source_key,
    ia_language: hbData.ia_language,
    source_type: hbData.source_type,
    compiled_at: hbData.compiled_at,
    helpers_hash: hbData.helpers_hash,
    needed_axis: hbData.needed_axis,
    use: hbData.use,
    temperature: hbData.temperature,
    dataformat: hbData.dataformat,
    error_message: hbData.error_message,
    is_valid: hbData.is_valid === 1 || hbData.is_valid === true
  };
}

/**
 * Charge un seul template Handlebars pré-compilé depuis l'API
 * Le template est déjà reconstruit par api-router.js (via processHbCompiledResponse)
 * Le cache est géré automatiquement par routedFetch
 * 
 * @param {string} iaLanguage - Code langue (FR, EN, ES, ...)
 * @param {string} templateKey - Clé du template (source_key)
 * @param {Object} [options] - Options de chargement
 *   - bypassCache: bool (force API call, skip cache)
 * @returns {Promise<Object|null>} Template compilé avec métadonnées ou null si non trouvé
 * 
 * @example
 * const template = await loadSingleTemplate('FR', 'Article@to_title');
 * if (template) {
 *   const result = template.compiled(articleData);
 * }
 */
export async function loadSingleTemplate(iaLanguage, templateKey, options = {}) {
  // Initialiser les helpers au premier appel
  if (!isHelpersInitialized) {
    await initializeHandlebarsHelpers();
  }

  debug.log(config, `[Handlebars] Loading single template: ${templateKey} (${iaLanguage})`);

  try {
    // Appel API pour récupérer le template spécifique
    // Encoder le template_key pour gérer les caractères spéciaux (#, @, etc.)
    const encodedTemplateKey = encodeURIComponent(templateKey);
    const apiUrl = `/api/config/read/hb-compiled?ia_language=${iaLanguage}&template_key=${encodedTemplateKey}`;
    
    // routedFetch retourne directement les données parsées avec cache automatique
    const data = await routedFetch(apiUrl, {}, options.bypassCache);

    if (!data.success || !data.templates || data.templates.length === 0) {
      debug.warn(config, `[Handlebars] Template not found: ${templateKey}`);
      return null;
    }

    // Extraire le premier (et unique) template
    const hbData = data.templates[0];
    
    // Vérifier que le template est valide
    if (!hbData.is_valid || !hbData.compiled) {
      debug.warn(config, `[Handlebars] Invalid template: ${templateKey}`);
      return null;
    }

    // Extraire les métadonnées
    const template = extractTemplateMetadata(hbData);

    debug.log(config, `[Handlebars] Template loaded successfully: ${templateKey}`);

    return template;
  } catch (error) {
    debug.error(config, `[Handlebars] Error loading template ${templateKey}:`, error);
    return null;
  }
}

/**
 * Exécute un template Handlebars
 * 
 * @param {Object} template - Template compilé (depuis loadSingleTemplate)
 * @param {Object} data - Données pour le template
 * @returns {string} Résultat rendu
 * 
 * @throws {Error} Si template invalide ou erreur exécution
 */
export async function renderTemplate(template, data = {}) {
  try {
    if (!template || !template.compiled) {
      throw new Error('Invalid template: no compiled function');
    }

    // Exécuter le template
    const result = template.compiled(data);

    return result;
  } catch (error) {
    debug.error(config, `[Handlebars] Error executing template ${template?.key}:`, error);
    throw error;
  }
}

/**
 * Récupère les métadonnées d'un template
 * @param {Object} template - Template compilé
 * @returns {Object|null} Métadonnées du template
 */
export function getTemplateMetadata(template) {
  if (!template) return null;

  return {
    key: template.key,
    ia_language: template.ia_language,
    source_type: template.source_type,
    compiled_at: template.compiled_at,
    helpers_hash: template.helpers_hash,
    needed_axis: template.needed_axis,
    use: template.use,
    temperature: template.temperature,
    dataformat: template.dataformat,
    error_message: template.error_message,
    is_valid: template.is_valid
  };
}

/**
 * Vide le cache mémoire
 */
export function clearTemplateCache() {
  debug.log(config, '[Handlebars] Clearing template cache');
  templateCache.clear();
}

/**
 * Pré-compile les templates source et les stocke dans la base de données
 * Workflow complet: Lecture → Pré-compilation → Stockage
 * 
 * @param {string} iaLanguage - Code langue (FR, EN, ES, ...)
 * @param {string|string[]} [sourceTypes] - Type(s) de source à pré-compiler (optionnel: tous si non fourni)
 * @param {Function} [onProgress] - Callback pour les messages de progression (optionnel)
 * @returns {Promise<Object>} Résultat {success, total, compiled, failed, errors}
 * 
 * @example
 * const result = await precompileAndStoreTemplates('FR', ['tab_action', 'ia_call'], (msg) => console.log(msg));
 * // Retourne: { success: true, total: 150, compiled: 150, failed: 0, errors: undefined }
 */
export async function precompileAndStoreTemplates(iaLanguage, sourceTypes = null, onProgress = null) {
  try {
    debug.log(config, `[Handlebars] Starting precompilation: language=${iaLanguage}, types=${sourceTypes || 'all'}`);
    
    if (onProgress) {
      onProgress('🚀 Démarrage de la précompilation des templates...');
    }

    // Initialiser les helpers une seule fois
    if (!isHelpersInitialized) {
      await initializeHandlebarsHelpers();
      if (onProgress) {
        onProgress('✓ Helpers Handlebars initialisés');
      }
    }

    // =====================================================================
    // ÉTAPE 1: Récupérer les templates source depuis l'API
    // =====================================================================
    debug.log(config, '[Handlebars] Step 1: Reading source templates from API...');
    
    if (onProgress) {
      onProgress('📖 Étape 1: Lecture des templates sources depuis l\'API...');
    }

    let apiUrl = `/api/config/read/templates-source?ia_language=${iaLanguage}`;
    if (sourceTypes) {
      const sourceTypesParam = Array.isArray(sourceTypes) 
        ? sourceTypes.join(',') 
        : sourceTypes;
      apiUrl += `&source_types=${sourceTypesParam}`;
    }

    const readData = await routedFetch(apiUrl, {}, true); // bypassCache=true

    // routedFetch retourne directement les données JSON, pas une Response
    if (!readData || !readData.success || !Array.isArray(readData.templates)) {
      const errorMsg = readData?.error || 'Invalid response from read endpoint';
      if (onProgress) {
        onProgress(`❌ Erreur: ${errorMsg}`);
      }
      throw new Error(errorMsg);
    }

    debug.log(config, `[Handlebars] Read ${readData.templates.length} source templates`);
    
    if (onProgress) {
      onProgress(`✓ ${readData.templates.length} template(s) source(s) chargé(s)`);
    }

    // =====================================================================
    // ÉTAPE 2: Pré-compiler les templates au niveau frontend
    // =====================================================================
    debug.log(config, '[Handlebars] Step 2: Pre-compiling templates...');
    
    if (onProgress) {
      onProgress('⚙️ Étape 2: Précompilation des templates...');
    }

    const compiledTemplates = [];
    const compilationErrors = [];

    for (const sourceTemplate of readData.templates) {
      let errorMsg = null;
      try {
        // Valider les données minimales
        if (!sourceTemplate.source_key || !sourceTemplate.source_text) {
          errorMsg = 'Missing source_key or source_text';
          compilationErrors.push({
            key: sourceTemplate.source_key || 'UNKNOWN',
            error: errorMsg
          });
          if (onProgress) {
            onProgress(`❌ ${sourceTemplate.source_key || 'UNKNOWN'}: ${errorMsg}`);
          }
          continue;
        }

        // Pré-compiler le template Handlebars en spec compilée
        const compiledSpec = Handlebars.precompile(sourceTemplate.source_text);

        // Stocker la spec compilée pour la transmission au backend
        compiledTemplates.push({
          source_key: sourceTemplate.source_key,
          compiled_spec: compiledSpec,
          ia_language: sourceTemplate.ia_language,
          source_type: sourceTemplate.source_type,
          use: sourceTemplate.use || null,
          temperature: sourceTemplate.temperature || null,
          dataformat: sourceTemplate.dataformat || null,
          needed_axis: analyzeNeededAxes(sourceTemplate.source_text),
          compiled_at: new Date().toISOString(),
          helpers_hash: generateHelpersHash(),
          error_message: null
        });

        debug.log(config, `[Handlebars] ✓ Precompiled: ${sourceTemplate.source_key}`);
        
        if (onProgress) {
          onProgress(`✓ ${sourceTemplate.source_key}: Précompilé avec succès`);
        }
      } catch (error) {
        errorMsg = error.message;
        compilationErrors.push({
          key: sourceTemplate.source_key,
          error: errorMsg
        });
        
        if (onProgress) {
          onProgress(`❌ ${sourceTemplate.source_key}: ${errorMsg}`);
        }
        
        // Ajouter même les templates échoués pour tracer les erreurs
        compiledTemplates.push({
          source_key: sourceTemplate.source_key,
          compiled_spec: null,
          ia_language: sourceTemplate.ia_language,
          source_type: sourceTemplate.source_type,
          use: sourceTemplate.use || null,
          temperature: sourceTemplate.temperature || null,
          dataformat: sourceTemplate.dataformat || null,
          needed_axis: analyzeNeededAxes(sourceTemplate.source_text),
          compiled_at: new Date().toISOString(),
          helpers_hash: generateHelpersHash(),
          error_message: errorMsg
        });
        
        debug.warn(config, `[Handlebars] ✗ Failed to compile ${sourceTemplate.source_key}: ${error.message}`);
      }
    }

    debug.log(config, `[Handlebars] Compiled ${compiledTemplates.length - compilationErrors.length} templates, ${compilationErrors.length} errors`);
    
    if (onProgress) {
      onProgress(`✓ Précompilation terminée: ${compiledTemplates.length - compilationErrors.length} succès, ${compilationErrors.length} erreur(s)`);
    }

    // =====================================================================
    // ÉTAPE 3: Stocker les templates pré-compilés dans la base de données
    // =====================================================================
    debug.log(config, '[Handlebars] Step 3: Storing compiled templates...');
    
    if (onProgress) {
      onProgress('💾 Étape 3: Stockage des templates compilés en base de données...');
    }

    const storeResponse = await routedFetch('/api/config/store/templates-compiled', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({ templates: compiledTemplates })
    });

    // routedFetch retourne directement les données JSON, pas une Response
    if (!storeResponse || !storeResponse.success) {
      const errorMsg = storeResponse?.error || 'Failed to store templates';
      if (onProgress) {
        onProgress(`❌ Erreur de stockage: ${errorMsg}`);
      }
      throw new Error(errorMsg);
    }

    debug.log(config, `[Handlebars] Stored ${storeResponse.stored} templates`);
    
    if (onProgress) {
      onProgress(`✓ ${storeResponse.stored} template(s) stocké(s) en base de données`);
    }

    // Vider le cache mémoire pour forcer un rechargement des templates pré-compilés
    clearTemplateCache();
    
    if (onProgress) {
      onProgress('✓ Cache mémoire vidé');
    }

    const finalErrors = compilationErrors.length > 0 ? compilationErrors : undefined;

    debug.log(config, `[Handlebars] Precompilation complete: total=${readData.templates.length}, compiled=${compiledTemplates.length}, stored=${storeResponse.stored}`);
    
    if (onProgress) {
      onProgress(`✅ Processus terminé: ${readData.templates.length} templates traités, ${compiledTemplates.length - compilationErrors.length} compilés avec succès`);
    }

    return {
      success: storeResponse.success && compilationErrors.length === 0,
      total: readData.templates.length,
      compiled: compiledTemplates.length,
      failed: compilationErrors.length + (storeResponse.failed || 0),
      errors: finalErrors || (storeResponse.errors ? storeResponse.errors : undefined)
    };

  } catch (error) {
    debug.error(config, '[Handlebars] Error in precompileAndStoreTemplates:', error);
    if (onProgress) {
      onProgress(`❌ Erreur fatale: ${error.message}`);
    }
    throw error;
  }
}

/**
 * Génère un hash simple basé sur les helpers enregistrés
 * Utilisé pour tracker les changements de configuration des helpers
 */
function generateHelpersHash() {
  // Hash basé sur la date du jour (simple mais efficace pour pré-compilation)
  const date = new Date();
  const dateStr = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
  return `h_${dateStr}`;
}

/**
 * Obtient les stats du cache
 */
export function getCacheStats() {
  const stats = {
    cachedLanguageTypes: Array.from(templateCache.keys()),
    totalCachedEntries: 0,
    totalCachedTemplates: 0
  };

  for (const templates of templateCache.values()) {
    stats.totalCachedEntries++;
    stats.totalCachedTemplates += Object.keys(templates).length;
  }

  return stats;
}

export default {
  initializeHandlebarsHelpers,
  loadSingleTemplate,
  renderTemplate,
  getTemplateMetadata,
  clearTemplateCache,
  precompileAndStoreTemplates,
  getCacheStats
};
